// ============================================================================
// CHANGO V5.0 — <AgreementModal> (T-D1-020 companion, §11.10 + §20 坑 #21)
// ============================================================================
// Shown on the Register page (and any other surface where the user must
// re-accept). Loads the current ToS + Privacy via
//   GET /api/legal/agreements?is_current=1
// and renders body_markdown sanitised through DOMPurify.
//
// Emits an onAccept(versions) callback with
//   { tos_version, privacy_version }
// so the Register form can include those exact strings in the POST body —
// §20 #21: the server will reject registration if the versions drift.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

import { api, HttpError } from '../api/client.js';

function useCurrentAgreements() {
  const [state, setState] = useState({ loading: true, tos: null, privacy: null, error: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/api/legal/agreements?is_current=1');
        const list = Array.isArray(res?.agreements) ? res.agreements : [];
        const tos = list.find((a) => a.agreement_type === 'tos') || null;
        const privacy = list.find((a) => a.agreement_type === 'privacy') || null;
        if (alive) setState({ loading: false, tos, privacy, error: null });
      } catch (err) {
        if (alive) setState({ loading: false, tos: null, privacy: null, error: err });
      }
    })();
    return () => { alive = false; };
  }, []);

  return state;
}

function renderMarkdown(md) {
  if (!md) return '';
  const rawHtml = marked.parse(md, { breaks: true, gfm: true });
  return DOMPurify.sanitize(rawHtml, { USE_PROFILES: { html: true } });
}

export default function AgreementModal({ open, onClose, onAccept }) {
  const { loading, tos, privacy, error } = useCurrentAgreements();
  const [tab, setTab] = useState('tos');
  const [confirmable, setConfirmable] = useState(false);
  const bodyRef = useRef(null);

  // Re-check whether the user has scrolled to the end — both agreements must
  // be viewed before the "我已阅读并同意" button is enabled. This is a soft
  // guard; the hard guard is server-side version matching.
  const [viewedTos, setViewedTos] = useState(false);
  const [viewedPrivacy, setViewedPrivacy] = useState(false);
  useEffect(() => {
    setConfirmable(viewedTos && viewedPrivacy && Boolean(tos?.version) && Boolean(privacy?.version));
  }, [viewedTos, viewedPrivacy, tos, privacy]);

  // When the content fits entirely without scrolling (short agreement or
  // fallback iframe), onScroll never fires — we'd wedge the button forever.
  // On tab-switch / content-change, check AT multiple moments whether the
  // scroll container is already at its bottom:
  //
  //   1. 50 ms timeout — covers the common case (marked → DOMPurify render is
  //      synchronous, browser paints within a frame).
  //   2. ResizeObserver on the scroll container — fires when content reflows
  //      async (font loading shifts heights, images load, iframe adjusts).
  //   3. onScroll handler — backstop for long docs where the user scrolls.
  //
  // All three paths funnel through the same `checkAtBottom()` helper so the
  // threshold stays consistent. `+4` slop absorbs sub-pixel rounding that
  // Chromium sometimes introduces on zoomed / DPR≠1 displays.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    const checkAtBottom = () => {
      if (!bodyRef.current) return;
      const node = bodyRef.current;
      // scrollHeight==clientHeight → short doc, always counts as "at bottom".
      const atBottom = node.scrollTop + node.clientHeight + 4 >= node.scrollHeight;
      if (!atBottom) return;
      if (tab === 'tos') setViewedTos(true);
      if (tab === 'privacy') setViewedPrivacy(true);
    };

    // Leg 1: single-frame deferred check after the new tab's content commits.
    const h = setTimeout(checkAtBottom, 50);

    // Leg 2: ResizeObserver catches late content reflow (fonts, images, iframe
    // onLoad, async markdown images). Guarded for older browsers that lack it.
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => checkAtBottom());
      ro.observe(el);
    }

    return () => {
      clearTimeout(h);
      if (ro) ro.disconnect();
    };
  }, [tab, tos, privacy]);

  const onScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight + 4 >= el.scrollHeight;
    if (!atBottom) return;
    if (tab === 'tos') setViewedTos(true);
    if (tab === 'privacy') setViewedPrivacy(true);
  };

  const current = tab === 'tos' ? tos : privacy;

  // body_markdown is the normal path. If it's the legacy placeholder (uploaded
  // before server-side extraction landed), fall back to rendering the PDF /
  // original file in an iframe so the tenant can still read + scroll. Also
  // helpful for scanned-image PDFs that have no extractable text.
  const bodyIsPlaceholder = Boolean(
    current?.body_markdown && /^（由文件上传/.test(current.body_markdown)
  );
  const pdfFallbackUrl = bodyIsPlaceholder
    ? (current?.file_pdf_url || (current?.file_type === 'pdf' ? current?.file_url : null))
    : null;

  const currentHtml = useMemo(() => {
    if (bodyIsPlaceholder) return ''; // iframe path instead
    const md = current?.body_markdown;
    return renderMarkdown(md);
  }, [bodyIsPlaceholder, current]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="agreement-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
    >
      <div className="card flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
          <h2 id="agreement-title" className="text-base font-semibold text-slate-800">
            服务协议与隐私政策
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-500 hover:bg-slate-100"
            aria-label="关闭"
          >
            ✕
          </button>
        </header>

        <nav className="flex gap-2 border-b border-slate-200 px-4 pt-3">
          <button
            type="button"
            className={`px-3 py-2 text-sm font-medium ${tab === 'tos' ? 'border-b-2 border-chango-500 text-chango-700' : 'text-slate-500 hover:text-slate-700'}`}
            onClick={() => setTab('tos')}
          >
            用户服务协议 {tos?.version ? <span className="text-xs text-slate-400">({tos.version})</span> : null}
          </button>
          <button
            type="button"
            className={`px-3 py-2 text-sm font-medium ${tab === 'privacy' ? 'border-b-2 border-chango-500 text-chango-700' : 'text-slate-500 hover:text-slate-700'}`}
            onClick={() => setTab('privacy')}
          >
            隐私政策 {privacy?.version ? <span className="text-xs text-slate-400">({privacy.version})</span> : null}
          </button>
        </nav>

        <div
          ref={bodyRef}
          onScroll={onScroll}
          className="flex-1 overflow-auto px-6 py-4 prose prose-slate max-w-none text-sm leading-6"
        >
          {loading ? (
            <p className="text-slate-500">加载中…</p>
          ) : error ? (
            <div className="alert-error">
              无法加载协议内容：{error instanceof HttpError ? error.message : '网络错误'}
            </div>
          ) : bodyIsPlaceholder && pdfFallbackUrl ? (
            // Legacy placeholder row — embed the PDF so users can still read it.
            <iframe
              key={pdfFallbackUrl}
              src={pdfFallbackUrl}
              title={tab === 'tos' ? '用户服务协议' : '隐私政策'}
              className="h-[60vh] w-full border-0"
              onLoad={() => {
                // iframe content isn't scrollable-by-parent; mark viewed once
                // loaded so the consent flow can progress.
                if (tab === 'tos') setViewedTos(true);
                if (tab === 'privacy') setViewedPrivacy(true);
              }}
            />
          ) : bodyIsPlaceholder ? (
            <div className="alert-error">
              协议内容尚未就绪（ops 侧未完成文档提取或预览）。请联系平台管理员。
            </div>
          ) : (
            // Markdown is sanitised via DOMPurify upstream.
            <div dangerouslySetInnerHTML={{ __html: currentHtml }} />
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-6 py-3">
          <p className="text-xs text-slate-500">
            {viewedTos && viewedPrivacy
              ? '✓ 您已阅读两份文档'
              : '请分别滚动至两份文档底部以启用同意按钮'}
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost">取消</button>
            <button
              type="button"
              disabled={!confirmable}
              onClick={() => onAccept && onAccept({
                tos_version: tos?.version,
                privacy_version: privacy?.version
              })}
              className="btn-primary"
            >
              我已阅读并同意
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
